import React from 'react';

/**
 * Minimalni Markdown renderer za tekst vijesti.
 *
 * Podržava ono što je potrebno za članke/intervjue:
 *   **podebljano**, *kurziv*, `kod`, [link](url)
 *   ## Podnaslov, ### Manji podnaslov
 *   - lista / 1. numerisana lista
 *   > citat
 *   --- horizontalna linija
 *
 * Renderuje se kroz React elemente (bez dangerouslySetInnerHTML), pa
 * unijeti HTML ostaje običan tekst i ne može biti izvršen.
 */

// **bold** | __bold__ | *italic* | _italic_ | `code` | [tekst](url)
const INLINE_PATTERN = String.raw`(\*\*|__)(?=\S)([\s\S]*?\S)\1|(\*|_)(?=\S)([\s\S]*?\S)\3|` + '`([^`]+)`' + String.raw`|\[([^\]]+)\]\(([^)\s]+)\)`;

const isSafeHref = (url) => /^(https?:\/\/|mailto:|\/)/i.test(url);

const parseInline = (text, keyPrefix = '') => {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let i = 0;

  // Preostali '\n' u tekstu je namjeran prelom reda (vidi withLineBreaks).
  // Radi se ovdje da bi prelom radio i unutar **podebljanog** ili linka.
  const pushText = (str, keySeed) => {
    str.split('\n').forEach((part, pi) => {
      if (pi > 0) nodes.push(<br key={`${keyPrefix}${keySeed}br${pi}`} />);
      if (part) nodes.push(part);
    });
  };

  // Svaki poziv dobija svoju instancu regexa - parseInline je rekurzivan,
  // pa bi dijeljeni /g regex resetovao lastIndex vanjskoj petlji.
  const re = new RegExp(INLINE_PATTERN, 'g');
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushText(text.slice(lastIndex, match.index), `t${i}`);
    }
    const key = `${keyPrefix}i${i++}`;

    if (match[2] !== undefined) {
      nodes.push(<strong key={key} className="font-semibold text-neutral-900">{parseInline(match[2], `${key}-`)}</strong>);
    } else if (match[4] !== undefined) {
      nodes.push(<em key={key} className="italic">{parseInline(match[4], `${key}-`)}</em>);
    } else if (match[5] !== undefined) {
      nodes.push(
        <code key={key} className="px-1.5 py-0.5 rounded-md bg-neutral-100 text-[0.9em] font-mono text-neutral-700">
          {match[5]}
        </code>
      );
    } else if (match[6] !== undefined) {
      const href = match[7];
      nodes.push(
        isSafeHref(href) ? (
          <a
            key={key}
            href={href}
            target={href.startsWith('/') ? undefined : '_blank'}
            rel="noopener noreferrer"
            className="text-brand-600 hover:text-brand-700 underline underline-offset-2"
          >
            {parseInline(match[6], `${key}-`)}
          </a>
        ) : (
          match[6]
        )
      );
    }

    lastIndex = re.lastIndex;
  }

  if (lastIndex < text.length) pushText(text.slice(lastIndex), 'end');
  return nodes;
};

// Red koji se završava rečeničnim znakom je namjeran prelom (autor je
// pritisnuo enter); red prekinut usred rečenice je posljedica "hard wrap"
// prelamanja pri copy-paste, pa se spaja sa sljedećim.
const isDeliberateBreak = (line) => /[.!?:;…„“”"»)\]]\s*$/.test(line);

const withLineBreaks = (text, keyPrefix) => {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  // Namjeran prelom ostaje '\n' (parseInline ga pretvara u <br />),
  // prelomljena rečenica se spaja razmakom u jedan tok teksta.
  const joined = lines.reduce(
    (acc, line, idx) => (idx === 0 ? line : acc + (isDeliberateBreak(lines[idx - 1]) ? '\n' : ' ') + line),
    ''
  );

  return parseInline(joined, keyPrefix);
};

/** Tekst vijesti -> niz blok elemenata. */
const renderBlocks = (text) => {
  const blocks = String(text || '').replace(/\r\n/g, '\n').split(/\n{2,}/);

  return blocks.map((raw, bi) => {
    const block = raw.trim();
    if (!block) return null;
    const key = `b${bi}`;
    const lines = block.split('\n');

    // --- horizontalna linija
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(block)) {
      return <hr key={key} className="my-8 border-neutral-200" />;
    }

    // ## Naslov (može biti prelomljen u više redova pri copy-paste)
    const heading = block.match(/^(#{1,4})\s+([\s\S]*)$/);
    if (heading) {
      const level = heading[1].length;
      const content = parseInline(
        heading[2].split('\n').map(l => l.trim()).join(' '),
        `${key}-`
      );
      if (level <= 2) {
        return (
          <h2 key={key} className="text-xl md:text-2xl font-bold text-neutral-900 mt-10 mb-4 leading-snug">
            {content}
          </h2>
        );
      }
      return (
        <h3 key={key} className="text-lg font-semibold text-neutral-900 mt-8 mb-3">
          {content}
        </h3>
      );
    }

    // > citat
    if (lines.every(l => /^\s*>/.test(l))) {
      const quote = lines.map(l => l.replace(/^\s*>\s?/, '')).join('\n');
      return (
        <blockquote key={key} className="my-6 pl-5 border-l-4 border-brand-200 text-neutral-700 italic">
          {withLineBreaks(quote, `${key}-`)}
        </blockquote>
      );
    }

    // - lista
    if (lines.every(l => /^\s*[-*+]\s+/.test(l))) {
      return (
        <ul key={key} className="my-4 space-y-2 list-disc pl-5 marker:text-brand-400">
          {lines.map((l, li) => (
            <li key={`${key}-${li}`}>{parseInline(l.replace(/^\s*[-*+]\s+/, ''), `${key}-${li}-`)}</li>
          ))}
        </ul>
      );
    }

    // 1. numerisana lista
    if (lines.every(l => /^\s*\d+[.)]\s+/.test(l))) {
      return (
        <ol key={key} className="my-4 space-y-2 list-decimal pl-5 marker:text-neutral-400">
          {lines.map((l, li) => (
            <li key={`${key}-${li}`}>{parseInline(l.replace(/^\s*\d+[.)]\s+/, ''), `${key}-${li}-`)}</li>
          ))}
        </ol>
      );
    }

    return (
      <p key={key} className="mb-5 last:mb-0">
        {withLineBreaks(block, `${key}-`)}
      </p>
    );
  });
};

/** Prikaz formatiranog teksta vijesti. */
export const Markdown = ({ text, className = '' }) => (
  <div className={className}>{renderBlocks(text)}</div>
);

/**
 * Markdown -> čist tekst, za kartice, meta description i sl.
 * gdje se prikazuje samo kratak isječak.
 */
export const stripMarkdown = (text) =>
  String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/^\s*#{1,6}\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+[.)]\s+/gm, '')
    .replace(/^\s*(-{3,}|\*{3,}|_{3,})\s*$/gm, '')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '$1')
    .replace(/(\*\*|__)(\S[\s\S]*?\S)\1/g, '$2')
    .replace(/(\*|_)(\S[\s\S]*?\S)\1/g, '$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\n{2,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export default Markdown;
